import { createContext, useContext } from 'react';
import { reportMissingProvider } from '../lib/missing-provider';
import type {
  SubscriptionManagementUrlResult,
  SubscriptionStateResult,
  SubscriptionSyncInput,
  SubscriptionWebCheckoutInput,
} from '@tabbio/types';
import type { CheckoutPurchaseOutcome } from '../lib/subscription-journey/checkout-flow';

export interface RevenueCatContextValue {
  isReady: boolean;
  isBusy: boolean;
  lastError: Error | null;
  syncSubscription: (input?: SubscriptionSyncInput) => Promise<SubscriptionStateResult | null>;
  /**
   * `purchased` = the store confirmed the charge (or the server already showed
   * the tier), `pending` = the store deferred it, `checkout` = web left for
   * hosted billing. Entitlement is only ever proven by a synced snapshot.
   */
  startPurchase: (input: SubscriptionWebCheckoutInput) => Promise<CheckoutPurchaseOutcome>;
  restorePurchases: (input?: SubscriptionSyncInput) => Promise<SubscriptionStateResult | null>;
  openManagementUrl: (companyId?: string | null) => Promise<SubscriptionManagementUrlResult | null>;
}

const RevenueCatContext = createContext<RevenueCatContextValue | null>(null);

// Not-ready fallback for frames rendered outside RevenueCatProvider during
// auth/route teardown transitions: read actions no-op, purchase throws only
// when actually invoked. Keeps the app shell from crashing to the root error
// boundary (same pattern as WorkspaceContext / SubscriptionContext).
const fallbackRevenueCatContextValue: RevenueCatContextValue = {
  isReady: false,
  isBusy: false,
  lastError: null,
  syncSubscription: async () => null,
  startPurchase: async () => {
    throw new Error('Purchases are unavailable right now. Please try again.');
  },
  restorePurchases: async () => null,
  openManagementUrl: async () => null,
};

export function useRevenueCat() {
  const context = useContext(RevenueCatContext);
  if (!context) {
    reportMissingProvider('RevenueCatProvider');
    return fallbackRevenueCatContextValue;
  }

  return context;
}

export { RevenueCatContext };
